import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Monitor,
  History,
  Bookmark,
  Save,
  Layers,
  RefreshCw,
  Plus,
  Trash2
} from 'lucide-react';
import type { Device } from '@/contexts/DeviceContext';

interface WorkspaceSidebarProps {
  devices: Device[];
  selectedDevice: string;
  onSelectDevice: (deviceId: string) => void;
  onAddDevice?: () => void;
  onDeleteDevice?: (deviceId: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  stats?: {
    openTabs?: number;
    savedTabs?: number;
    historyItems?: number;
    bookmarks?: number;
  };
  currentPage?: 'tabs' | 'history' | 'bookmarks';
}

export function WorkspaceSidebar({
  devices,
  selectedDevice,
  onSelectDevice,
  onAddDevice,
  onDeleteDevice,
  onRefresh,
  isLoading = false,
  stats = {},
  currentPage = 'tabs'
}: WorkspaceSidebarProps) {
  const { t } = useTranslation();

  const totalTabs = devices.reduce((sum, d) => sum + (d.tabCount || 0), 0);

  return (
    <div className="w-72 space-y-4">
      {/* Stats Overview */}
      <div className="rounded-xl border border-white/5 bg-[#16161e] p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9a9aac] mb-3">
          {t('workspace.overview') || 'Overview'}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-white/3 border border-white/5">
            <div className="flex items-center gap-2 text-[#9a9aac] mb-1">
              <Monitor size={14} />
              <span className="text-xs">{t('workspace.devices') || 'Devices'}</span>
            </div>
            <p className="text-xl font-semibold text-white">{devices.length}</p>
          </div>
          <div className="p-3 rounded-lg bg-white/3 border border-white/5">
            <div className="flex items-center gap-2 text-[#9a9aac] mb-1">
              <Layers size={14} />
              <span className="text-xs">{t('workspace.openTabs') || 'Open Tabs'}</span>
            </div>
            <p className="text-xl font-semibold text-white">{stats.openTabs ?? totalTabs}</p>
          </div>
          <div className="p-3 rounded-lg bg-white/3 border border-white/5">
            <div className="flex items-center gap-2 text-[#9a9aac] mb-1">
              <Save size={14} />
              <span className="text-xs">{t('workspace.saved') || 'Saved'}</span>
            </div>
            <p className="text-xl font-semibold text-white">{stats.savedTabs ?? '-'}</p>
          </div>
          <div className="p-3 rounded-lg bg-white/3 border border-white/5">
            <div className="flex items-center gap-2 text-[#9a9aac] mb-1">
              {currentPage === 'history' ? <History size={14} /> : <Bookmark size={14} />}
              <span className="text-xs">
                {currentPage === 'history' ? (t('workspace.history') || 'History') : (t('workspace.bookmarks') || 'Bookmarks')}
              </span>
            </div>
            <p className="text-xl font-semibold text-white">
              {currentPage === 'history' ? (stats.historyItems ?? '-') : (stats.bookmarks ?? '-')}
            </p>
          </div>
        </div>
      </div>

      {/* Device Selector */}
      <div className="rounded-xl border border-white/5 bg-[#16161e] p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9a9aac]">
            {t('workspace.selectDevice') || 'Select Device'}
          </h3>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-1 rounded text-[#9a9aac] hover:text-white hover:bg-white/5 transition-all disabled:opacity-50"
              title={t('workspace.refresh') || 'Refresh'}
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <div className="h-10 rounded-lg bg-white/5 animate-pulse" />
            <div className="h-10 rounded-lg bg-white/5 animate-pulse" />
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-[#9a9aac] mb-3">
              {t('workspace.noDevices') || 'No devices synced yet'}
            </p>
            {onAddDevice && (
              <button
                onClick={onAddDevice}
                className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-brand-primary hover:bg-brand-primary/10 border border-brand-primary/30 transition-all"
              >
                <Plus size={14} />
                {t('workspace.installExtension') || 'Install Extension'}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {/* All Devices option */}
            <button
              onClick={() => onSelectDevice('all')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                selectedDevice === 'all'
                  ? 'bg-brand-primary/10 text-white border border-brand-primary/30'
                  : 'text-[#a0a0b0] hover:text-white hover:bg-white/5 border border-transparent'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-linear-to-br/srgb from-brand-primary/20 to-iris-500/20 flex items-center justify-center">
                <Layers size={16} className="text-brand-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{t('workspace.allDevices') || 'All Devices'}</p>
                <p className="text-xs text-[#9a9aac]">{totalTabs} {t('workspace.tabs') || 'tabs'}</p>
              </div>
            </button>

            {/* Individual devices */}
            {devices.map((device) => (
              <div key={device.id} className="group relative">
                <button
                  onClick={() => onSelectDevice(device.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                    selectedDevice === device.id
                      ? 'bg-brand-primary/10 text-white border border-brand-primary/30'
                      : 'text-[#a0a0b0] hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-white/6 flex items-center justify-center">
                    <Monitor size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{device.deviceName}</p>
                      {device.isCurrentDevice && (
                        <span className="text-[12px] px-1.5 py-0.5 rounded bg-brand-primary/20 text-brand-primary">
                          {t('workspace.current') || 'Current'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#9a9aac]">
                      {device.browser} · {device.tabCount ?? 0} {t('workspace.tabs') || 'tabs'}
                    </p>
                  </div>
                </button>
                {onDeleteDevice && !device.isCurrentDevice && (
                  <button
                    onClick={() => onDeleteDevice(device.id)}
                    className="absolute ltr:right-2 rtl:left-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-[#9a9aac] hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                    title={t('workspace.removeDevice') || 'Remove device'}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}

            {/* Add device */}
            {onAddDevice && (
              <button
                onClick={onAddDevice}
                className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-lg text-sm text-brand-primary hover:bg-brand-primary/10 transition-all"
              >
                <Plus size={14} />
                {t('workspace.addDevice') || 'Add Device'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Quick Tips */}
      <div className="rounded-xl border border-white/5 bg-[#16161e] p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9a9aac] mb-2">
          {t('workspace.tip') || 'Tip'}
        </h3>
        <p className="text-xs text-[#a0a0b0] leading-relaxed">
          {currentPage === 'tabs' && (t('workspace.tabsTip') || 'Select a device to filter tabs, or view all devices combined.')}
          {currentPage === 'history' && (t('workspace.historyTip') || 'Browse your browsing history across all synced devices.')}
          {currentPage === 'bookmarks' && (t('workspace.bookmarksTip') || 'Access bookmarks synced from your browser extension.')}
        </p>
      </div>
    </div>
  );
}

export default WorkspaceSidebar;
